/**
 * AdminPersonProfile — 360° super-admin view of a single Person.
 *
 * Sections: Header · Memberships · Workflow Events · Documents produced ·
 * Finance transactions
 *
 * Route: /admin/persons/:id
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue, SelectGroup, SelectLabel } from '@/components/ui/select';
import { useChurchRoles } from '@/lib/churchRoles';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  UserCircle, ArrowLeft, Mail, Phone, ShieldCheck, Activity, FileText,
  Wallet, Building2, RefreshCw, Download, Bell,
} from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const fmtMoney = (amount, cur) => {
  if (amount === null || amount === undefined) return '—';
  return `${(cur || 'USD')} ${Number(amount).toFixed(2)}`;
};

const StatBox = ({ icon: Icon, label, value, tone = 'slate', testid }) => {
  const tones = {
    slate: 'from-slate-500/15 to-slate-500/5 text-slate-300 border-slate-400/30',
    violet: 'from-violet-500/15 to-violet-500/5 text-violet-300 border-violet-400/30',
    emerald: 'from-emerald-500/15 to-emerald-500/5 text-emerald-300 border-emerald-400/30',
    amber: 'from-amber-500/15 to-amber-500/5 text-amber-300 border-amber-400/30',
  };
  return (
    <div className={`rounded-xl border bg-gradient-to-br p-4 ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider opacity-80">{label}</span>
        <Icon className="h-4 w-4 opacity-70" />
      </div>
      <div className="mt-2 text-2xl font-semibold">{value ?? '—'}</div>
    </div>
  );
};

export default function AdminPersonProfile() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [attaching, setAttaching] = useState(false);

  const profileQ = useQuery({
    queryKey: ['person-profile', id],
    queryFn: async () => {
      const r = await axios.get(
        `${API}/api/admin/identity-registry/persons/${id}/profile?limit=50`,
        { headers: authH() },
      );
      return r.data;
    },
  });

  if (profileQ.isLoading) {
    return <div className="min-h-screen bg-slate-950 p-6 text-slate-400" data-testid="profile-loading">Loading person profile…</div>;
  }
  if (profileQ.isError) {
    return (
      <div className="min-h-screen bg-slate-950 p-6 text-rose-300" data-testid="profile-error">
        Person not found.
        <div className="mt-4">
          <Button onClick={() => navigate('/admin/persons')} variant="outline" className="border-slate-700 bg-slate-900">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to directory
          </Button>
        </div>
      </div>
    );
  }

  const person = profileQ.data?.person || {};
  const memberships = profileQ.data?.memberships || [];
  const workspaces = profileQ.data?.workspaces || [];
  const workflowEvents = profileQ.data?.workflow_events || [];
  const documents = profileQ.data?.documents || [];
  const finance = profileQ.data?.finance || { total: 0, items: [] };
  const financeByCur = finance.items.reduce((acc, r) => {
    const c = r.currency || 'USD';
    acc[c] = (acc[c] || 0) + Number(r.amount || 0);
    return acc;
  }, {});

  const displayName = (person.first_name || person.last_name)
    ? `${person.first_name || ''} ${person.last_name || ''}`.trim()
    : (person.name || person.username || 'Unnamed');
  return (
    <div className="min-h-screen bg-slate-950 p-6 text-slate-100" data-testid="admin-person-profile-page">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button
            data-testid="back-to-persons-btn"
            variant="outline"
            className="border-slate-700 bg-slate-900 hover:bg-slate-800"
            onClick={() => navigate('/admin/persons')}
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Directory
          </Button>
          <div className="flex gap-2">
            <Button
              data-testid="profile-notif-prefs-btn"
              variant="outline"
              className="border-slate-700 bg-slate-900 hover:bg-slate-800"
              onClick={() => navigate(`/admin/notifications/preferences/${id}`)}
            >
              <Bell className="mr-2 h-4 w-4" /> Preferences
            </Button>
            <Button
              data-testid="profile-download-pdf-btn"
              className="bg-violet-600 text-white hover:bg-violet-500"
              onClick={async () => {
                try {
                  const res = await fetch(
                    `${API}/api/admin/identity-registry/persons/${id}/profile.pdf`,
                    { headers: authH() },
                  );
                  if (!res.ok) throw new Error(`HTTP ${res.status}`);
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  const cd = res.headers.get('content-disposition') || '';
                  const m = cd.match(/filename="([^"]+)"/);
                  a.download = m ? m[1] : `person_profile_${id}.pdf`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  setTimeout(() => URL.revokeObjectURL(url), 1000);
                } catch (err) {
                  alert(`Download failed: ${err.message}`);
                }
              }}
            >
              <Download className="mr-2 h-4 w-4" /> Download PDF
            </Button>
            <Button
              data-testid="profile-refresh-btn"
              variant="outline"
              className="border-slate-700 bg-slate-900 hover:bg-slate-800"
              onClick={() => profileQ.refetch()}
            >
              <RefreshCw className="mr-2 h-4 w-4" /> Refresh
            </Button>
          </div>
        </div>

        {/* Person Card */}
        <Card className="border-slate-800 bg-slate-900/60">
          <CardContent className="flex flex-col gap-4 p-6 md:flex-row md:items-center">
            <UserCircle className="h-20 w-20 text-violet-400" />
            <div className="flex-1">
              <h1 className="text-3xl font-bold" data-testid="person-name">{displayName}</h1>
              <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-400">
                {person.email && (
                  <span className="inline-flex items-center gap-1" data-testid="person-email">
                    <Mail className="h-4 w-4" /> {person.email}
                  </span>
                )}
                {person.phone_e164 && (
                  <span className="inline-flex items-center gap-1" data-testid="person-phone">
                    <Phone className="h-4 w-4" /> {person.phone_e164}
                  </span>
                )}
                {person.username && (
                  <span className="text-slate-500">@{person.username}</span>
                )}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {workspaces.map((w) => (
                  <Badge key={w.key} variant="outline" className="border-violet-500/40 text-violet-300" data-testid="workspace-badge">
                    <Building2 className="mr-1 h-3 w-3" /> {w.label || w.key}
                  </Badge>
                ))}
                {profileQ.data?.is_super_admin && (
                  <Badge className="bg-amber-500/20 text-amber-300" data-testid="super-admin-badge">
                    <ShieldCheck className="mr-1 h-3 w-3" /> Super Admin
                  </Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatBox testid="stat-memberships" icon={ShieldCheck} label="Memberships" value={memberships.length} tone="violet" />
          <StatBox testid="stat-workflow" icon={Activity} label="Workflow events" value={workflowEvents.length} tone="slate" />
          <StatBox testid="stat-documents" icon={FileText} label="Documents produced" value={documents.length} tone="emerald" />
          <StatBox testid="stat-finance" icon={Wallet} label="Finance rows" value={finance.total} tone="amber" />
        </div>

        {/* Memberships */}
        <Card className="border-slate-800 bg-slate-900/60">
          <CardHeader className="flex flex-row items-center justify-between gap-3">
            <CardTitle className="text-base">Memberships ({memberships.length})</CardTitle>
            <Button size="sm" variant="outline" className="border-slate-700 bg-slate-900 hover:bg-slate-800"
                    onClick={() => setAttaching(true)} data-testid="person-attach-btn">
              Add to a church or level
            </Button>
          </CardHeader>
          <CardContent>
            {memberships.length === 0 ? (
              <div className="py-4 text-sm text-slate-500" data-testid="memberships-empty">No active memberships.</div>
            ) : (
              <table className="w-full text-left text-sm" data-testid="memberships-table">
                <thead>
                  <tr className="border-b border-slate-800 text-xs uppercase tracking-wider text-slate-400">
                    <th className="py-2 pr-4">Workspace</th>
                    <th className="py-2 pr-4">Role type</th>
                    <th className="py-2 pr-4">Role</th>
                    <th className="py-2 pr-4">Tenant</th>
                    <th className="py-2 pr-4">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {memberships.map((m) => (
                    <tr key={m.id} className="border-b border-slate-800/60" data-testid="membership-row">
                      <td className="py-2 pr-4">{m.workspace || '—'}</td>
                      <td className="py-2 pr-4">{m.role_type || '—'}</td>
                      <td className="py-2 pr-4">{m.role || '—'}</td>
                      <td className="py-2 pr-4 text-slate-400">{m.tenant_id || '—'}</td>
                      <td className="py-2 pr-4 text-slate-500">{m.role_source_collection || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* Workflow Events */}
        <Card className="border-slate-800 bg-slate-900/60">
          <CardHeader><CardTitle className="text-base">Recent Workflow Events ({workflowEvents.length})</CardTitle></CardHeader>
          <CardContent>
            {workflowEvents.length === 0 ? (
              <div className="py-4 text-sm text-slate-500" data-testid="workflow-empty">No workflow events for this person.</div>
            ) : (
              <table className="w-full text-left text-sm" data-testid="workflow-table">
                <thead>
                  <tr className="border-b border-slate-800 text-xs uppercase tracking-wider text-slate-400">
                    <th className="py-2 pr-4">When</th>
                    <th className="py-2 pr-4">Event type</th>
                    <th className="py-2 pr-4">Entity</th>
                    <th className="py-2 pr-4">Tenant</th>
                  </tr>
                </thead>
                <tbody>
                  {workflowEvents.map((e) => (
                    <tr key={e.id} className="border-b border-slate-800/60" data-testid="workflow-row">
                      <td className="py-2 pr-4 text-slate-300">{fmtDate(e.occurred_at)}</td>
                      <td className="py-2 pr-4 font-medium">{e.event_type}</td>
                      <td className="py-2 pr-4 text-slate-400">{e.entity_type || '—'} · {e.entity_id || '—'}</td>
                      <td className="py-2 pr-4 text-slate-500">{e.tenant_id || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* Documents produced */}
        <Card className="border-slate-800 bg-slate-900/60">
          <CardHeader><CardTitle className="text-base">Documents produced ({documents.length})</CardTitle></CardHeader>
          <CardContent>
            {documents.length === 0 ? (
              <div className="py-4 text-sm text-slate-500" data-testid="documents-empty">No documents on record.</div>
            ) : (
              <table className="w-full text-left text-sm" data-testid="documents-table">
                <thead>
                  <tr className="border-b border-slate-800 text-xs uppercase tracking-wider text-slate-400">
                    <th className="py-2 pr-4">When</th>
                    <th className="py-2 pr-4">Document type</th>
                    <th className="py-2 pr-4">Module</th>
                    <th className="py-2 pr-4">Filename</th>
                    <th className="py-2 pr-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((d) => (
                    <tr key={d.id} className="border-b border-slate-800/60" data-testid="document-row">
                      <td className="py-2 pr-4 text-slate-300">{fmtDate(d.created_at)}</td>
                      <td className="py-2 pr-4 font-medium">{d.document_type || d.template}</td>
                      <td className="py-2 pr-4"><Badge variant="outline" className="border-slate-700 text-slate-300">{d.source_module}</Badge></td>
                      <td className="py-2 pr-4 text-slate-500">{d.filename || '—'}</td>
                      <td className="py-2 pr-4">
                        {d.success
                          ? <span className="text-emerald-400">ok</span>
                          : <span className="text-rose-400" title={d.error}>failed</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* Finance */}
        <Card className="border-slate-800 bg-slate-900/60">
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between">
              <span>Finance transactions ({finance.total})</span>
              <span className="text-xs font-normal text-slate-400">
                {Object.entries(financeByCur).map(([c, a]) => `${c} ${a.toFixed(2)}`).join(' · ') || '—'}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {finance.items.length === 0 ? (
              <div className="py-4 text-sm text-slate-500" data-testid="finance-empty">No finance transactions linked to this person.</div>
            ) : (
              <table className="w-full text-left text-sm" data-testid="finance-table">
                <thead>
                  <tr className="border-b border-slate-800 text-xs uppercase tracking-wider text-slate-400">
                    <th className="py-2 pr-4">When</th>
                    <th className="py-2 pr-4">Category</th>
                    <th className="py-2 pr-4">Workspace</th>
                    <th className="py-2 pr-4">Direction</th>
                    <th className="py-2 pr-4">Amount</th>
                    <th className="py-2 pr-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {finance.items.map((r) => (
                    <tr key={r.id} className="border-b border-slate-800/60" data-testid="finance-row">
                      <td className="py-2 pr-4 text-slate-300">{fmtDate(r.occurred_at)}</td>
                      <td className="py-2 pr-4">{r.category}</td>
                      <td className="py-2 pr-4">{r.scope?.workspace || '—'}</td>
                      <td className="py-2 pr-4">
                        <span className={r.direction === 'income' ? 'text-emerald-400' : 'text-rose-400'}>
                          {r.direction}
                        </span>
                      </td>
                      <td className="py-2 pr-4 font-medium">{fmtMoney(r.amount, r.currency)}</td>
                      <td className="py-2 pr-4 text-slate-400">{r.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>

      {attaching && (
        <AttachToUnitDialog person={person} personId={id}
                            onClose={() => setAttaching(false)}
                            onSaved={() => { profileQ.refetch(); setAttaching(false); }} />
      )}
    </div>
  );
}

/**
 * Put this person straight onto a unit's roll — a province, region, district or
 * assembly. No application: they are already in the directory.
 */
function AttachToUnitDialog({ person, personId, onClose, onSaved }) {
  const [unitId, setUnitId] = useState('');
  const [role, setRole] = useState('member');
  const [mode, setMode] = useState('also');
  const [saving, setSaving] = useState(false);
  const { groups: roleGroups } = useChurchRoles();

  const unitsQ = useQuery({
    queryKey: ['attach-units'],
    queryFn: async () => (await axios.get(`${API}/api/admin/churches?is_active=true`,
                                          { headers: authH() })).data,
  });
  const units = unitsQ.data || [];

  const submit = async () => {
    setSaving(true);
    try {
      const name = (person.full_name || person.name
        || `${person.first_name || ''} ${person.last_name || ''}`).trim();
      const res = await axios.post(`${API}/api/church/${unitId}/members/assign`, {
        person_id: personId,
        first_name: person.first_name || name.split(' ')[0] || '',
        last_name: person.last_name || name.split(' ').slice(1).join(' '),
        email: person.email || '',
        phone: person.phone || person.phone_e164 || '',
        role, mode, status: 'active',
      }, { headers: authH() });
      toast.success(`${res.data.name} added to the roll`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add them');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-slate-800 bg-slate-900 text-slate-100" data-testid="person-attach-dialog">
        <DialogHeader>
          <DialogTitle>Add to a church or level</DialogTitle>
          <DialogDescription className="text-slate-400">
            They join the roll straight away — no application, at any level of the hierarchy.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-400">Unit</label>
            <Select value={unitId} onValueChange={setUnitId}>
              <SelectTrigger className="border-slate-700 bg-slate-950" data-testid="person-attach-unit">
                <SelectValue placeholder="Choose a province, district or assembly" />
              </SelectTrigger>
              <SelectContent>
                {units.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name} <span className="text-[10px] capitalize text-slate-400 ml-1">{u.level}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-400">Role</label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="border-slate-700 bg-slate-950" data-testid="person-attach-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {roleGroups.map((g) => (
                  <SelectGroup key={g.key}>
                    <SelectLabel className="text-[10px] uppercase tracking-wide text-slate-400">{g.label}</SelectLabel>
                    {(g.roles || []).map((r) => (
                      <SelectItem key={r.key} value={r.key} data-testid={`person-attach-role-${r.key}`}>{r.label}</SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant={mode === 'also' ? 'default' : 'outline'}
                    className={mode === 'also' ? '' : 'border-slate-700 bg-slate-950'}
                    onClick={() => setMode('also')} data-testid="person-attach-also">Also serve here</Button>
            <Button size="sm" variant={mode === 'transfer' ? 'default' : 'outline'}
                    className={mode === 'transfer' ? '' : 'border-slate-700 bg-slate-950'}
                    onClick={() => setMode('transfer')} data-testid="person-attach-transfer">Transfer here</Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="border-slate-700 bg-slate-950" onClick={onClose}>Cancel</Button>
          <Button disabled={!unitId || saving} onClick={submit} data-testid="person-attach-save">
            {saving ? 'Adding…' : 'Add to roll'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
